/**
 * Wordings of «Учитель» v3 — family «praise» (docs/TEACHING-V3.md §2.5, §5, §6.5; pool specs: ../spec.ts).
 * Rules: no Latin, NO squares (the board shows them), Гамбитик speaks of himself as a boy, the child through {g:…},
 * no «молодец», no clock words; «вот эти клетки / этот фланг / сюда» only in pools with a board cue.
 *
 * Praise is ONE sentence, ≤ 10 words, said right after the child's move, in the PAST tense, and names what exactly was
 * good. The child's past verb always goes through {g:…} and never right after «ты» (the lint reads «ты …л» in the
 * expanded masculine text as a bare form) — «{g:Сам нашёл|Сама нашла}», «{g:придумал|придумала} ты», or no «ты».
 *
 * Truth, as core `lesson/praise.ts` fires the reasons:
 *  - `mate` also follows an arrow: never «сам»;
 *  - `treasureFound`: the target / piece hints of §2.7 may have come, the arrow did not — «до стрелки», never «без подсказки»;
 *  - `own.*`: an arrow may have pointed at another move — never «без стрелки», «без подсказки», «без моей помощи»;
 *  - `freeCapture` = nobody can LEGALLY take back (the defender may be pinned, or the king may «guard» a covered square):
 *    «даром», «взять в ответ нельзя» — never «без защиты», «никто не защищал», «одинокий»;
 *  - `winMaterial` = defended, the capture still wins: never «без защиты»;
 *  - `recaptured` may only limit a loss: never «материал на месте»;
 *  - `escaped` / `defended` / `centerPawn` = the piece stands safe by exchange (it may still be attacked): «в безопасности»,
 *    «взять невыгодно» — never «не достать»;
 *  - `kingActive` = forward, to the centre OR to the pawns: never «вперёд»;
 *  - `own.center` = attacking the enemy centre pawn OR supporting ours: never «центр стал крепче»;
 *  - `tactic.fork` ply2 names no piece (the forker may be another piece) and points at nothing (no cue yet).
 */
import type { LessonStage, LessonWordings } from '../types.ts';

const Y: readonly [LessonStage, LessonStage] = [1, 2];
const O: readonly [LessonStage, LessonStage] = [3, 5];

export const WORDINGS: LessonWordings = {
  // ───────────────────────── tactics (always, found by the child; «начал комбинацию» for a fork two plies later) ─────────────────────────
  'v3.praise.mate': [
    // never «партия выиграна / победа»: `v3.end.win` follows right after
    { t: 'Мат — королю соперника некуда было деться!' },
    { t: 'Твой шах оказался матом!' },
    { t: 'Ни убежать, ни закрыться, ни съесть — мат!' },
    { t: 'Шах и мат — атака дошла до самого конца!' },
    { t: 'Ура, мат — король соперника попался!' },
    { t: 'Шах — и королю соперника уже ничто не поможет!' },
    { t: 'Мат поставлен — точка в партии!' },
    { t: 'Красивый финал: мат королю соперника!' },
    { t: 'Этим ходом партия и закончилась — мат!' },
  ],
  'v3.praise.tactic.fork': [
    // the fork is on the board now; stages 1–2 say «двойной удар», «вилка» from stage 2
    { t: 'Двойной удар: {конь} {p:напал|напала} сразу на двоих!', when: ['now'] },
    { t: 'Одним ходом {конь} {p:ударил|ударила} в две стороны!', when: ['now'] },
    { t: '{Твой} {конь} держит под ударом сразу двоих — красота!', when: ['now'] },
    { t: 'Настоящая вилка: {конь} {p:нацелился|нацелилась} на две фигуры!', when: ['now'], stages: [2, 5] },
    { t: '{g:Сам нашёл|Сама нашла} вилку — двоих сразу не спасти!', when: ['now'], stages: [2, 5] },
    // a check or a capture first, the fork on the next move (no piece named: the forker may be another piece)
    { t: '{g:Начал|Начала} хитрую комбинацию — дальше может получиться двойной удар!', when: ['ply2'] },
    { t: 'Ход с хитростью: скоро может выйти двойной удар!', when: ['ply2'] },
    { t: 'Первый шаг сделан — за ним прячется двойной удар!', when: ['ply2'] },
    { t: 'Комбинация началась: следом может быть вилка!', when: ['ply2'], stages: [2, 5] },
    { t: 'Ход с прицелом: {g:подготовил|подготовила} вилку!', when: ['ply2'], stages: [2, 5] },
  ],
  'v3.praise.tactic.pin': [
    // «связка» is a stage-3 word: stages 1–2 say «застрял», «на привязи», «за спиной кто-то важнее»
    { t: 'Теперь {конь} соперника {p:застрял|застряла}: за {его} спиной кто-то важнее!' },
    { t: '{Конь} соперника {p:замер|замерла} — уходить {ему} теперь опасно!' },
    { t: 'Ага, {конь} соперника {p:оказался|оказалась} щитом для фигуры поважнее!' },
    { t: 'Как гвоздём {g:прибил|прибила} {коня} к месту!' },
    { t: '{Конь} соперника теперь как на привязи!' },
    { t: 'Получилась связка: {конь} соперника прикрывает того, кто сзади!', stages: O },
    { t: '{g:Связал|Связала} {коня} по рукам и ногам!', stages: O },
  ],
  'v3.praise.tactic.skewer': [
    { t: '{g:Напал|Напала} на важную фигуру — а за ней стоит ещё одна!' },
    { t: 'Две фигуры соперника на одной линии попали под удар!' },
    { t: 'Кто впереди — уйдёт, а кто сзади — попадётся!' },
    { t: 'Удар прошёл насквозь — до той фигуры, что сзади!' },
    { t: '{g:Нашёл|Нашла} сквозной удар — задняя фигура не спрячется!', stages: O },
    { t: 'Настоящий сквозной удар — через одну фигуру к другой!', stages: O },
  ],
  'v3.praise.tactic.discovered': [
    { t: 'Одна фигура отошла — и открыла дорогу другой!' },
    { t: 'Хитро {g:сдвинул|сдвинула} одну фигуру, а напала другая!' },
    { t: 'Засада сработала: дорога открылась, и в бой вступила фигура сзади!' },
    { t: 'Фигура шагнула в сторону — и из-за неё выглянула атака!' },
    { t: 'Вскрытое нападение — {g:открыл|открыла} линию для дальнобойной фигуры!', stages: [4, 5] },
    { t: 'Получилось вскрытое нападение — атака из-за спины!', stages: [4, 5] },
  ],
  'v3.praise.tactic.removeDefender': [
    // «защитник» is a stage-4 word: before that «охрана», «сторож»
    { t: '{g:Съел|Съела} охранника — теперь доберёмся и до другой фигуры!' },
    { t: 'Охрана съедена — и у соперника зашаталась оборона!' },
    { t: 'Сначала {g:убрал|убрала} охрану, а добыча — следом!' },
    { t: 'Был сторож — и нет сторожа: теперь очередь другой фигуры!' },
    { t: '{g:Убрал|Убрала} защитника — теперь другую фигуру не удержать!', stages: [4, 5] },
    { t: 'Точный расчёт: сначала защитник, потом то, что он охранял!', stages: [4, 5] },
  ],
  'v3.praise.tactic.trapped': [
    { t: '{p:Попался|Попалась} {конь} — бежать {ему} некуда!' },
    { t: '{g:Поймал|Поймала} {коня} в ловушку — все дорожки опасны!' },
    { t: 'Хлоп — и {конь} соперника {p:остался|осталась} без выхода!' },
    { t: 'Как в мышеловке: {конь} соперника {p:оказался|оказалась} в западне!' },
    { t: 'Соперник не спасёт {коня} — {g:перекрыл|перекрыла} все пути!' },
  ],
  'v3.praise.tactic.doubleCheck': [
    { t: '{g:Объявил|Объявила} двойной шах — королю остаётся только бежать!' },
    { t: 'Два шаха сразу — одним ходом {g:ударил|ударила} дважды!' },
    { t: 'Получился двойной шах — закрыться от двух сразу нельзя!' },
    { t: 'Королю соперника пришлось туго: шах с двух сторон!' },
    { t: 'Хитрый ход: шах дали две фигуры вместе!' },
  ],

  // ───────────────────────── the treasure found alone (never «без подсказки»: the hints may have come — only «до стрелки») ─────────────────────────
  'v3.praise.treasureFound': [
    { t: 'Есть подарок — {g:сам нашёл|сама нашла}!', stages: Y },
    { t: 'Ура, {g:отыскал|отыскала} мой подарок!', stages: Y },
    { t: 'Подарок не ускользнул — у тебя зоркие глаза!', stages: Y },
    { t: 'Как сыщик: {g:выследил|выследила} подарок {g:сам|сама}!', stages: Y },
    { t: 'Нашёлся подарок — {g:увидел|увидела} его раньше стрелки!', stages: Y },
    { t: 'Я загадал подарок — и он уже у тебя!', stages: Y },
    { t: 'Подарок найден — стрелка даже не понадобилась!', stages: O },
    { t: 'Я спрятал подарок, но от тебя он не укрылся!', stages: O },
    { t: 'Всё {g:посчитал|посчитала} {g:сам|сама} — подарок раскрыт!', stages: O },
    { t: 'Глаз-алмаз: спрятанный подарок теперь наш!', stages: O },
    { t: '{g:Разглядел|Разглядела} то, что я спрятал, — какое внимание!', stages: O },
    { t: 'Загадка решена: {g:нашёл|нашла} ход, который я задумал!', stages: O },
    { t: '{g:Искал|Искала} — и {g:нашёл|нашла}: подарок твой!' },
    { t: 'Секретный подарок больше не секрет!' },
    { t: 'Раз — и мой подарок уже у нас!' },
  ],

  // ───────────────────────── routine reasons: only a move the child made himself, once per game ─────────────────────────
  'v3.praise.freeCapture': [
    // nobody can LEGALLY take back — the defender may be pinned or the king's square covered: never «без защиты»
    { t: '{g:Съел|Съела} {коня} — а взять в ответ соперник не может!', stages: Y },
    { t: 'Ням, {конь} соперника {p:достался|досталась} нам даром!', stages: Y },
    { t: '{p:Бесплатный|Бесплатная} {конь} — {g:увидел|увидела} и {g:забрал|забрала}!', stages: Y },
    { t: 'Зоркий глаз: {g:заметил|заметила}, что за {коня} платить не придётся!', stages: Y },
    { t: 'Недоглядел соперник — а мы забрали {коня} бесплатно!', stages: Y },
    { t: 'Чистый выигрыш — {конь} соперника {p:взят|взята} без размена!', stages: O },
    { t: '{g:Проверил|Проверила}, что взять в ответ нельзя, — и {g:забрал|забрала} {коня}!', stages: O },
    { t: '{Коня} забрали без риска — ответного взятия не будет!', stages: O },
    { t: '{g:Прибрал|Прибрала} {коня} к рукам — и совсем бесплатно!', stages: O },
    { t: 'Соперник {коня} не уберёг — {g:воспользовался|воспользовалась} сразу!', stages: O },
    { t: 'Соперник оставил {коня} под ударом — а мы тут как тут!' },
    { t: 'Хоп — и {конь} соперника {p:исчез|исчезла}, а мы ничего не отдали!' },
    { t: 'Сразу {g:углядел|углядела} лёгкую добычу — {коня}!' },
  ],
  'v3.praise.winMaterial': [
    // defended, but the capture still wins: never «без защиты»
    { t: '{Коня} защищали, а выгода всё равно наша!' },
    { t: 'Выгодно {g:побил|побила} {коня} — даже после ответа мы впереди!' },
    { t: '{g:Сосчитал|Сосчитала}, кто кого защищает, — и {g:взял|взяла} {коня}!' },
    { t: 'Размен выгоден нам — {коня} {g:забрал|забрала} с умом!' },
    { t: 'Точный удар: {коня} взяли, а отдадим меньше!' },
    { t: 'Охрана {коня:gen} не помогла — счёт в нашу пользу!' },
    { t: 'Верный расчёт: {конь} соперника того {p:стоил|стоила}!' },
  ],
  'v3.praise.stoppedMate': [
    // true for any way of stopping the mate (a block, a guard, a capture, a king step)
    { t: 'Угрозы мата больше нет — вовремя {g:защитился|защитилась}!' },
    { t: 'Соперник целился в мат — а мы успели помешать!' },
    { t: 'Мата не будет — защита получилась крепкой!' },
    { t: 'Отбились от мата — король спокоен!' },
    { t: 'Ловушку с матом {g:разгадал|разгадала} и {g:закрыл|закрыла}!' },
    { t: 'Опасный мат {g:заметил|заметила} и не {g:пустил|пустила}!' },
    { t: 'Мат не пройдёт — соперника {g:остановил|остановила} ты!' },
  ],
  'v3.praise.answeredCheck': [
    // true for every way out: a king move, a block, a capture — and never «из трёх» (not all three may exist)
    { t: 'Шах отбит — ответ {g:выбрал|выбрала} верный!' },
    { t: 'Из всех способов спастись {g:нашёл|нашла} точный!' },
    { t: '{g:Не растерялся|Не растерялась} от шаха — король спасён!' },
    { t: 'Спокойно {g:ответил|ответила} на шах — без суеты!' },
    { t: 'Шах позади — король цел и спокоен!' },
    { t: 'Правильно {g:спас|спасла} короля от шаха!' },
    { t: 'Король в беде не остался — {g:сам|сама} {g:придумал|придумала} выход!' },
  ],
  'v3.praise.escaped': [
    // never the king (`escape` skips it); «в безопасности», never «не достать»
    { t: '{Конь} {p:убежал|убежала} из-под удара — {p:цел и невредим|цела и невредима}!', stages: Y },
    { t: '{g:Спас|Спасла} {коня} — {он} теперь в безопасности!', stages: Y },
    { t: 'Шмыг — и {конь} {p:спрятался|спряталась} от удара!', stages: Y },
    { t: 'Никого не отдали — {g:увёл|увела} {коня} в тихое место!', stages: Y },
    { t: 'Вжух — и {конь} {p:ушёл|ушла} от беды!', stages: Y },
    { t: 'Соперник хотел съесть {коня}, а {он} {p:ускользнул|ускользнула}!', stages: Y },
    { t: '{g:Увёл|Увела} {коня} из-под удара на безопасную клетку!', stages: O },
    { t: 'Вовремя {g:заметил|заметила} угрозу — {конь} уже не под ударом!', stages: O },
    { t: 'Материал цел: {конь} {p:увернулся|увернулась} от удара!', stages: O },
    { t: 'Угрозу {g:отвёл|отвела}: {конь} теперь стоит надёжно!', stages: O },
    { t: 'Соперник остался ни с чем: {конь} {p:ушёл|ушла} вовремя!', stages: O },
    { t: 'Удар пришёлся в пустоту — {конь} уже в другом месте!' },
    { t: 'Спасение удалось — {конь} {p:остался|осталась} с нами!' },
    { t: 'Ловко {g:спрятал|спрятала} {коня} от нападения!' },
  ],
  'v3.praise.defended': [
    // «защитник» is a stage-4 word: «охрана», «охранник», «под защитой»
    { t: '{g:Защитил|Защитила} {коня} — теперь {он} не {p:один|одна}!', stages: Y },
    { t: '{Твой} {конь} {p:получил|получила} верного охранника!', stages: Y },
    { t: 'Друг пришёл на помощь — {конь} под защитой!', stages: Y },
    { t: '{g:Прикрыл|Прикрыла} {коня} — в обиду не дадим!', stages: Y },
    { t: 'Теперь за {коня} есть кому заступиться!', stages: Y },
    { t: 'Вовремя {g:подстраховал|подстраховала} {коня}!', stages: O },
    { t: 'Защита поставлена — {конь} больше не висит!', stages: O },
    { t: 'Сразу {g:увидел|увидела} нападение на {коня} и {g:ответил|ответила} защитой!', stages: O },
    { t: 'Теперь брать {коня} невыгодно — защита сработала!', stages: O },
    { t: 'Напали на {коня} — а {он} уже под защитой!', stages: O },
    { t: '{g:Позаботился|Позаботилась} о своих — {конь} в надёжных руках!' },
    { t: '{Конь} {p:был|была} под ударом, а теперь под охраной!' },
    { t: 'Наша фигура была в беде — и её выручили!' },
  ],
  'v3.praise.castled': [
    // never «теперь можно спокойно атаковать»
    { t: 'Король доволен: у него теперь есть домик!', stages: Y },
    { t: 'Рокировка сделана — король спрятан, ладья в игре!', stages: Y },
    { t: 'Домик для короля {g:построил|построила} ты — и вовремя!', stages: Y },
    { t: 'Король и ладья сделали хитрый прыжок!', stages: Y },
    { t: 'Ура, у короля появился домик!', stages: Y },
    { t: 'Вовремя {g:сделал|сделала} рокировку — в центре королю опасно!', stages: O },
    { t: '{g:Сам|Сама} {g:решил|решила}, что пора рокироваться, — и точно!', stages: O },
    { t: 'Рокировка как по учебнику — спокойно и без спешки!', stages: O },
    { t: 'Два дела одним ходом: король укрылся, ладья проснулась!', stages: O },
    { t: 'Правило дебюта выполнено — королю стало спокойнее!', stages: O },
    { t: 'Король сменил адрес — теперь он в уголке!' },
    { t: 'Про королевский домик {g:вспомнил|вспомнила} {g:сам|сама}!' },
    { t: 'Короля — в домик, ладью — в бой!' },
  ],
  'v3.praise.knightFirst': [
    { t: '{g:Начал|Начала} с коня — слоны подождут!' },
    { t: 'Сначала конь, потом слон — {g:выбрал|выбрала} верный порядок!' },
    { t: 'Конь вышел первым — так поступают опытные игроки!' },
    { t: 'Первым в бой отправился конь — хорошее правило!' },
    { t: 'Коня — вперёд, слона — потом: {g:сыграл|сыграла} по правилу!' },
    { t: 'Конь прыгнул раньше слона — так и надо!' },
    { t: 'Из коней и слонов первым {g:разбудил|разбудила} коня!' },
  ],
  'v3.praise.developed': [
    // a knight or a bishop from home, not to the edge, safe. «развитие» from stage 2 (here: the older band); never «в центр»
    { t: '{g:Разбудил|Разбудила} {коня} — {он} уже в игре!', stages: Y },
    { t: '{Конь} {p:вышел|вышла} из дома — одним бойцом больше!', stages: Y },
    { t: 'Команда растёт: {конь} {p:встал|встала} в строй!', stages: Y },
    { t: 'Дома пустеет — фигуры идут в бой!', stages: Y },
    { t: 'Ещё одна фигура спешит на помощь своим!', stages: Y },
    { t: 'Развитие идёт: {конь} {p:вышел|вышла} на хорошее место!', stages: O },
    { t: '{g:Вывел|Вывела} {коня} не на край, а поближе к делу!', stages: O },
    { t: '{Конь} {p:занял|заняла} позицию — дебют идёт по правилам!', stages: O },
    { t: 'Так и развивают фигуры: {конь} {p:покинул|покинула} начальную клетку!', stages: O },
    { t: 'Ещё один боец на месте — {конь} уже работает!', stages: O },
    { t: 'Фигур в игре стало больше, и это твоя работа!' },
    { t: '{Конь} {p:вступил|вступила} в игру по твоей команде!' },
    { t: 'Новая фигура в бою — {g:сам|сама} {g:вывел|вывела}!' },
  ],
  'v3.praise.centerPawn': [
    // the pawn stands safe by exchange (it may still be attacked): «в безопасности», «взять невыгодно», never «не достать»
    { t: 'Пешка встала в центр — {g:сам|сама} {g:придумал|придумала}!', stages: Y },
    { t: 'Наша пешка заняла главное место на доске!', stages: Y },
    { t: 'Ура, {g:занял|заняла} центр пешкой!', stages: Y },
    { t: 'Топ-топ — пешка пришла в самый центр!', stages: Y },
    { t: '{g:Поставил|Поставила} пешку прямо в центр, и она там в безопасности!', stages: Y },
    { t: 'Центральная пешка встала надёжно — взять её невыгодно!', stages: O },
    { t: 'Кусочек центра наш — {g:сам|сама} {g:решил|решила} туда шагнуть!', stages: O },
    { t: 'Центр укрепился: пешка шагнула вперёд без риска!', stages: O },
    { t: 'Пешка встала в центре — оттуда она держит важные клетки!', stages: O },
    { t: 'Первое правило дебюта — центр, и пешка уже там!', stages: O },
    { t: 'Пешка в центре — и это была твоя идея!' },
    { t: 'Шаг в центр {g:сделал|сделала} {g:сам|сама}!' },
    { t: 'Посреди доски твёрдо встала наша пешка!' },
  ],
  'v3.praise.recaptured': [
    // a recapture may only limit a loss (a bishop back for a rook): never «материал на месте», «поровну»
    { t: '{g:Забрал|Забрала} {коня} в ответ — размен состоялся!' },
    { t: 'Соперник взял — а мы тут же ответили!' },
    { t: 'Поменялись: за нашу фигуру соперник отдал {коня}!' },
    { t: 'Своё вернули сразу — ход {g:нашёл|нашла} точный!' },
    { t: 'Взятие за взятие — {конь} соперника не {p:ушёл|ушла} безнаказанно!' },
    { t: 'Раз — и {конь} соперника тоже {p:покинул|покинула} доску!' },
    { t: 'Не {g:дал|дала} сопернику уйти с добычей — {g:взял|взяла} {коня}!' },
  ],

  // ───────────────────────── rare reasons ─────────────────────────
  'v3.praise.connectedRooks': [
    // «связанные ладьи» would be a stage-3 word: «увидели друг друга», «соединились»
    { t: 'Ура, ладьи теперь заодно!' },
    { t: 'Две ладьи подали друг другу руку!' },
    { t: 'Дорога между ладьями свободна — {g:расчистил|расчистила} {g:сам|сама}!' },
    { t: 'Теперь ладьи соединились и работают парой!' },
    { t: 'Ладья с ладьёй — теперь не разлей вода!' },
    { t: 'Горизонталь свободна — ладьи соединились!', stages: O },
  ],
  'v3.praise.rookOpenFile': [
    // only the `open` variant (no pawns at all on the file); «открытая линия», «вертикаль» from stage 3
    { t: 'Ладья вышла на свободную дорогу — пешки ей не мешают!' },
    { t: 'Ладье открылся простор — теперь она стреляет далеко!' },
    { t: 'Для ладьи {g:нашёл|нашла} дорогу без пешек!' },
    { t: 'Ладья вырвалась на волю — пешек на её линии нет!' },
    { t: 'Хорошая дорожка для ладьи — ни одной пешки!' },
    { t: 'Открытая линия занята нашей ладьёй!', stages: O },
    { t: 'На открытой вертикали ладья — как дома!', stages: O },
  ],
  'v3.praise.passedPush': [
    // «проходная» is a stage-5 word
    { t: 'Вперёд, пешка — до последнего ряда стало ближе!' },
    { t: 'Наша пешка шагнула вперёд, и чужие пешки ей не помеха!' },
    { t: 'Пешка побежала в ферзи!' },
    { t: '{g:Толкнул|Толкнула} пешку вперёд — будущий ферзь растёт!' },
    { t: 'Маленький шаг пешки — большой шаг к ферзю!' },
    { t: 'Проходная пошла — сопернику пора её ловить!', stages: [5, 5] },
    { t: 'Проходную надо двигать — так и {g:сделал|сделала}!', stages: [5, 5] },
  ],
  'v3.praise.kingActive': [
    // the king goes forward, to the centre OR to the pawns: never «вперёд»; «эндшпиль», «активный король» are stage-5 words
    { t: 'Король смело взялся за дело — фигур-то мало!' },
    { t: 'Король превратился в бойца — {g:сам|сама} {g:повёл|повела} его!' },
    { t: '{g:Отправил|Отправила} короля в поход — в конце партии он силач!' },
    { t: 'Навстречу приключениям зашагал наш король!' },
    { t: 'Королём {g:шагнул|шагнула} смело — прятаться ему больше не нужно!' },
    { t: 'Эндшпиль — время короля, и он уже в пути!', stages: [5, 5] },
    { t: 'Вовремя {g:сделал|сделала} короля активным — в эндшпиле это важно!', stages: [5, 5] },
  ],
  'v3.praise.tradeAhead': [
    { t: 'Больше материала — меньше фигур: {g:разменялся|разменялась} с умом!' },
    { t: 'Мы впереди, и размен сделал нашу победу ближе!' },
    { t: '{g:Вспомнил|Вспомнила} правило: кто впереди — тот меняется!' },
    { t: 'Чем меньше фигур, тем ценнее наш перевес — так и {g:сыграл|сыграла}!' },
    { t: '{g:Упростил|Упростила} игру, пока мы впереди, — очень умно!' },
    { t: 'Лишний материал стал весомее — размен сделан вовремя!' },
  ],
  'v3.praise.promotion': [
    { t: 'Пешка дошла до конца и превратилась в ферзя!' },
    { t: 'Ура, появился новый ферзь!' },
    { t: 'Маленькая пешка выросла в ферзя!' },
    { t: 'Пешка прошла весь путь — и получила корону!' },
    { t: 'Превращение состоялось — пешка стала самой сильной фигурой!' },
    { t: 'Путь до последнего ряда пройден — встречайте ферзя!' },
    { t: '{g:Провёл|Провела} пешку через всю доску — какой путь!' },
  ],

  // ───────────────────────── own good moves (not the advice): the child's own idea, never «без стрелки» ─────────────────────────
  'v3.praise.own.attack': [
    { t: '{g:Сам придумал|Сама придумала} напасть на {коня}!' },
    { t: 'Своя идея: {конь} соперника {p:попал|попала} под удар!' },
    { t: 'Своим умом {g:нашёл|нашла}, как напасть на {коня}!' },
    { t: 'Твоя атака началась: {конь} соперника {p:занервничал|занервничала}!' },
    { t: 'Нападение на {коня} {g:придумал|придумала} ты, не я!' },
    { t: '{g:Сам|Сама} {g:выбрал|выбрала} цель — {конь} соперника под прицелом!' },
    { t: 'Смело {g:пошёл|пошла} в атаку на {коня}!' },
  ],
  'v3.praise.own.develop': [
    // a knight or a bishop from home, late in the game (after the opening plies)
    { t: '{Коня} {g:вывел|вывела} по своей задумке — и {он} уже в деле!' },
    { t: '{p:Засиделся|Засиделась} {конь} дома — а теперь и {он} в игре!' },
    { t: 'Твоя идея — и {конь} {p:вышел|вышла} на работу!' },
    { t: '{g:Сам решил|Сама решила}, кого будить, — {конь} уже шагает!' },
    { t: '{Конь} {p:покинул|покинула} дом — и это было твоё решение!' },
    { t: 'Ещё одна фигура в бою — по твоему плану!' },
    { t: 'Ход {конём} {g:придумал|придумала} {g:сам|сама}!' },
  ],
  'v3.praise.own.center': [
    // true both for attacking the enemy centre pawn and for supporting ours (a pawn move): never «центр стал крепче»
    { t: 'Бороться за центр {g:придумал|придумала} {g:сам|сама}!' },
    { t: '{g:Сам|Сама} {g:позаботился|позаботилась} о центре!' },
    { t: 'О центре {g:подумал|подумала} {g:сам|сама} — а это главное!' },
    { t: 'По-своему, но точно — в центре прибавилось сил!' },
    { t: 'По твоей задумке пешка помогает центру!' },
    { t: 'Центр важен — и ты это знаешь!' },
    { t: 'Твоя идея — и в центре стало жарче!' },
  ],
  'v3.praise.own.defend': [
    // true for a block, a guard and a step away (the pool's purpose: «защитил или увёл фигуру»)
    { t: '{g:Сам|Сама} {g:заметил|заметила} угрозу и {g:спас|спасла} фигуру!' },
    { t: 'Фигура спасена — и {g:придумал|придумала} это ты!' },
    { t: 'Беду от фигуры {g:отвёл|отвела} {g:сам|сама}!' },
    { t: 'Опасность {g:увидел|увидела} вовремя — и {g:справился|справилась}!' },
    { t: 'Твой собственный ход уберёг фигуру!' },
    { t: 'Соперник метил в нашу фигуру — и промахнулся!' },
    { t: 'Никого не потеряли — и решение было твоё!' },
  ],
  'v3.praise.own.plan': [
    { t: 'Ход по нашему плану — и {g:нашёл|нашла} его {g:сам|сама}!' },
    { t: '{g:Вспомнил|Вспомнила} нашу тему и {g:сыграл|сыграла} по ней!' },
    { t: 'План сделал шаг вперёд — твой шаг!' },
    { t: '{g:Сам продолжил|Сама продолжила} наш план!' },
    { t: 'Этот ход — из нашего плана, и выбор был твой!' },
    { t: 'Своим умом {g:дошёл|дошла} до хода по нашей теме!' },
    { t: 'Тема партии ожила — и это твой ход!' },
  ],
  'v3.praise.own.good': [
    // the engine agrees, no clear idea found: praise the thinking, not «хороший ход»; the «Сам» nudge may have come
    { t: '{g:Сам подумал|Сама подумала} — и я с тобой согласен!' },
    { t: 'Этот ход — целиком твоя придумка!' },
    { t: 'Мне понравилось, как ты думаешь над ходом!' },
    { t: 'Своя идея, свой ход — так и растёт шахматист!' },
    { t: 'Шахматист думает сам — так и {g:сделал|сделала}!' },
    { t: 'До этого хода {g:додумался|додумалась} своим умом!' },
    { t: 'У тебя свой взгляд на доску — и он верный!' },
    { t: 'Своими глазами {g:увидел|увидела}, своей головой {g:решил|решила}!' },
    { t: 'Подумать перед ходом — вот что ты уже умеешь!' },
  ],

  // ───────────────────────── habits (about once a week instead of the routine reason) ─────────────────────────
  'v3.praise.habit.knightFirst': [
    { t: '{g:Начал|Начала} с коня — у тебя это уже привычка мастера!' },
    { t: 'Конь раньше всех — как в твоих прошлых партиях!' },
    { t: 'Сначала конь — это уже вошло у тебя в привычку!' },
    { t: 'Конь раньше слона — ты делаешь так всегда, я заметил!' },
    { t: 'Правило про коней стало твоим собственным!' },
  ],
  'v3.praise.habit.castled': [
    { t: 'Король вовремя спрятался — ты всегда об этом помнишь!' },
    { t: 'Домик для короля — твоя привычка, я уже заметил!' },
    { t: 'Рокировка вовремя — ты делаешь так раз за разом!' },
    { t: 'Прятать короля ты уже умеешь — привычка чемпиона!' },
    { t: 'Рокировка для тебя уже как почистить зубы — привычное дело!' },
  ],
  'v3.praise.habit.centerPawn': [
    // the reason is any safe pawn step to the centre, not only the first move: never «партия началась с…»
    { t: 'Шаг пешкой в центр — это уже твой почерк!' },
    { t: '{g:Занял|Заняла} центр пешкой — как всегда!' },
    { t: 'Центр — первое, о чём ты думаешь в партии!' },
    { t: 'Пешка шагнула в центр — у тебя так всегда!' },
  ],
  'v3.praise.habit.developed': [
    { t: 'Быстро выводить фигуры — это уже твоя привычка!' },
    { t: '{g:Вывел|Вывела} фигуру без раздумий — так ты играешь всегда!' },
    { t: 'Быстрое развитие — твоя сильная сторона, я это заметил!', stages: [2, 5] },
    { t: 'Фигуры у тебя выходят в бой дружно — это твой стиль!' },
    { t: 'Будить фигуры пораньше — это ты уже умеешь!' },
  ],
};
